import type { DragEvent as ReactDragEvent } from 'react';
import { useState } from 'react';

/**
 * The native drag of an ordered list, for the NPC's sources and the benches' NPC (npc.md §3.2, §5).
 *
 * No library, and the same split as `reglages/global-rules.tsx` and the protocol timeline: the GRIP
 * is the drag SOURCE and the ROW is the drop target, which keeps a drag from starting inside a
 * textarea and stealing a selection. The arrows (« Monter », « Descendre ») are the keyboard's way,
 * and they NAME what they move where a handle names nothing; this hook is only the pointer's.
 *
 * `onMove(key, index)` is told where the dragged row goes in the list WITHOUT it - the arithmetic
 * the two spellings of a drop disagree on, done once here.
 */

type Drag = { readonly key: string; readonly overKey: string | null; readonly after: boolean };

export interface RowDrag {
  /** The key being dragged, or null. */
  readonly dragging: string | null;
  /** Where a drop would land: the row it is over and whether below its middle. */
  readonly hint: { readonly key: string; readonly after: boolean } | null;
  /** Props of the grip: `draggable` and its two handlers. */
  readonly grip: (key: string) => {
    draggable: true;
    onDragStart: () => void;
    onDragEnd: () => void;
  };
  /** Props of a row, the drop target. */
  readonly row: (key: string) => {
    onDragOver: (event: ReactDragEvent<HTMLElement>) => void;
    onDrop: (event: ReactDragEvent<HTMLElement>) => void;
  };
}

export function useRowDrag(keys: readonly string[], onMove: (key: string, index: number) => void) {
  const [drag, setDrag] = useState<Drag | null>(null);

  function drop(targetKey: string): void {
    if (drag === null) return;
    const moved = drag.key;
    const after = drag.after;
    setDrag(null);
    if (moved === targetKey) return;
    const rest = keys.filter((key) => key !== moved);
    const at = rest.indexOf(targetKey);
    if (at < 0) return;
    onMove(moved, after ? at + 1 : at);
  }

  const result: RowDrag = {
    dragging: drag?.key ?? null,
    hint: drag !== null && drag.overKey !== null ? { key: drag.overKey, after: drag.after } : null,
    grip: (key) => ({
      draggable: true,
      onDragStart: () => setDrag({ key, overKey: null, after: false }),
      onDragEnd: () => setDrag(null),
    }),
    row: (key) => ({
      onDragOver: (event) => {
        if (drag === null) return;
        event.preventDefault();
        const box = event.currentTarget.getBoundingClientRect();
        const after = event.clientY > box.top + box.height / 2;
        setDrag((was) => (was === null ? null : { ...was, overKey: key, after }));
      },
      onDrop: (event) => {
        event.preventDefault();
        drop(key);
      },
    }),
  };
  return result;
}
